import React from "react";
import { Link } from "react-router-dom";
import {
  Compass, TrendingUp, Building2, ArrowRight, CheckCircle2,
  Briefcase, Globe2, KeyRound, ScrollText,
} from "lucide-react";
import { Reveal } from "../components/Reveal";
import Seo from "../components/Seo";
import { IMAGES } from "../lib/site";

const SECTIONS = [
  {
    id: "home-search",
    icon: Compass,
    eyebrow: "Service 01",
    title: "Personalised Home Search & Advisory",
    blurb:
      "We listen first — to your goal, your timeline, your family. Then we build a tight, evidence-backed shortlist and walk the road to possession alongside you.",
    image: IMAGES.interior,
    points: [
      "Curated project shortlisting based on your brief",
      "Accompanied site visits and on-the-ground evaluation",
      "Price negotiation support with developer-direct access",
      "Loan structuring and documentation assistance",
      "End-to-end advisory from booking to possession",
    ],
    flip: false,
    icons: KeyRound,
  },
  {
    id: "investment",
    icon: TrendingUp,
    eyebrow: "Service 02",
    title: "Real Estate Investment Expertise",
    blurb:
      "Real estate is a wealth-building lever — when treated as one. We bring micro-market intelligence, pre-launch entry and exit strategies usually reserved for institutional desks.",
    image: IMAGES.apartment,
    points: [
      "High-growth micro-market identification",
      "Pre-launch and early-stage investment opportunities",
      "Rental-yield optimisation strategies",
      "Capital appreciation planning across 3–7 year horizons",
      "NRI and passive investor portfolios with quarterly reviews",
    ],
    flip: true,
    icons: Globe2,
  },
  {
    id: "commercial",
    icon: Building2,
    eyebrow: "Service 03",
    title: "Commercial Real Estate (CRE) Solutions",
    blurb:
      "From Grade-A pre-leased assets to high-street retail, we source commercial opportunities with rigorous asset-performance underwriting.",
    image: IMAGES.advisory,
    points: [
      "Grade-A office space investments",
      "Pre-leased commercial assets with stable yields",
      "Retail and high-street opportunities",
      "Asset performance and tenant-credit analysis",
      "Long-term yield and exit strategy planning",
    ],
    flip: false,
    icons: Briefcase,
  },
];

const Services = () => (
  <>
    <Seo
      title="Real Estate Advisory Services — Home Search, Investment, CRE"
      path="/services"
      description="Personalised home search, real estate investment expertise, and commercial real estate solutions across Bengaluru and Dubai — by the My Hoom advisory desk."
    />
    {/* Sub-hero */}
    <section className="relative bg-navy text-white py-28 lg:py-36 overflow-hidden">
      <div className="absolute inset-0 opacity-30">
        <img src={IMAGES.office} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/70 to-navy" />
      </div>
      <div className="container-tight relative z-10">
        <Reveal>
          <span className="eyebrow text-gold">Our services</span>
          <h1 className="mt-3 font-serif text-5xl sm:text-6xl lg:text-7xl max-w-3xl leading-[1.05]">
            Advisory built for outcomes — <span className="text-gold">not transactions</span>.
          </h1>
          <p className="mt-7 text-white/75 max-w-2xl text-lg leading-relaxed">
            Three offerings, one philosophy: deep diligence, honest counsel, and a hand-held experience from the first call to the final signature.
          </p>
        </Reveal>
      </div>
    </section>

    {/* SECTIONS */}
    {SECTIONS.map((s, idx) => (
      <section
        key={s.id}
        id={s.id}
        data-testid={`service-section-${s.id}`}
        className={`${idx % 2 === 1 ? "bg-[#FAFAFA]" : "bg-white"} py-24 lg:py-32 scroll-mt-24`}
      >
        <div className="container-tight">
          <div className={`grid lg:grid-cols-12 gap-12 items-center ${s.flip ? "lg:[&>*:first-child]:order-2" : ""}`}>
            <Reveal className="lg:col-span-7">
              <span className="eyebrow">{s.eyebrow}</span>
              <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-navy leading-tight">{s.title}</h2>
              <p className="mt-5 text-navy/65 text-lg leading-relaxed max-w-2xl">{s.blurb}</p>
              <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3.5">
                {s.points.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm text-navy/80">
                    <CheckCircle2 className="text-gold mt-0.5 shrink-0" size={16} strokeWidth={2} />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact" data-testid={`service-cta-${s.id}`} className="btn-navy mt-10 text-sm">
                Discuss your goal <ArrowRight size={16} />
              </Link>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden card-lift">
                <img src={s.image} alt={s.title} loading="lazy" className="w-full h-[420px] object-cover" />
                <div className="absolute top-5 left-5 h-12 w-12 rounded-xl bg-white/95 text-navy grid place-items-center">
                  <s.icon size={20} strokeWidth={1.8} />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    ))}

    {/* PROCESS STRIP */}
    <section className="bg-navy text-white py-24 lg:py-28">
      <div className="container-tight">
        <Reveal className="max-w-2xl">
          <span className="eyebrow text-gold">The My Hoom Process</span>
          <h2 className="mt-3 font-serif text-4xl text-white">A simple, repeatable journey.</h2>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-4 gap-6">
          {[
            { n: "01", t: "Discovery call", d: "Understand goal, timeline, budget and risk." },
            { n: "02", t: "Curated shortlist", d: "3–6 hand-picked options with thesis." },
            { n: "03", t: "Site visits & diligence", d: "Independent verification on the ground." },
            { n: "04", t: "Close & beyond", d: "Negotiate, document, possess. We stay." },
          ].map((p, i) => (
            <Reveal key={p.n} delay={i * 0.08} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
              <ScrollText className="text-gold" size={20} strokeWidth={1.6} />
              <div className="mt-4 font-serif text-gold text-sm tracking-widest">{p.n}</div>
              <h3 className="mt-2 font-serif text-2xl text-white">{p.t}</h3>
              <p className="mt-2 text-sm text-white/70 leading-relaxed">{p.d}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  </>
);

export default Services;
